import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/auth";
import { Helmet } from "react-helmet-async";
import { IconCaretLeft, IconUser, IconShieldLock, IconCheck, IconAlertCircle } from "@tabler/icons-react";
import countries from "../data/countries";

const Profile = () => {
  const navigate = useNavigate();

  // --- profile state ---
  const [formData, setFormData] = useState({ firstname: "", lastname: "", country: "" });
  const [initialData, setInitialData] = useState({ firstname: "", lastname: "", country: "" });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  // --- password state ---
  const [passwordData, setPasswordData] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [pwErrors, setPwErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);

  // --- messages ---
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // --- computed ---
  const initials = useMemo(() => {
    const f = (formData.firstname || "").trim();
    const l = (formData.lastname || "").trim();
    const fi = f ? f[0].toUpperCase() : "";
    const li = l ? l[0].toUpperCase() : "";
    return (fi + li) || "U";
  }, [formData.firstname, formData.lastname]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetchWithAuth("/api/auth/profile");
        const data = await res.json();
        const payload = {
          firstname: data?.firstname || "",
          lastname: data?.lastname || "",
          country: data?.country || "",
        };
        setFormData(payload);
        setInitialData(payload);
      } catch {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleProfileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateProfile = () => {
    if (!formData.firstname.trim() || !formData.lastname.trim() || !formData.country.trim()) {
      setError("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!validateProfile()) return;

    setSavingProfile(true);
    const cleanData = {
      firstname: formData.firstname.trim(),
      lastname: formData.lastname.trim(),
      country: formData.country.trim(),
    };

    try {
      const response = await fetchWithAuth("/api/auth/update-profile", {
        method: "PUT",
        body: JSON.stringify(cleanData),
      });
      if (response.ok) {
        setMessage("✅ Profile updated successfully");
        setInitialData(cleanData);
      } else {
        const err = await response.json();
        setError(`❌ Error: ${err?.detail || "Unable to update profile"}`);
      }
    } catch {
      setError("❌ Network error");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleResetProfile = () => {
    setFormData(initialData);
    setMessage("");
    setError("");
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const validatePassword = () => {
    const errs = {};
    if (!passwordData.old_password.trim()) errs.old_password = "Enter your current password.";
    if (!passwordData.new_password.trim()) errs.new_password = "Enter a new password.";
    if (!passwordData.confirm_password.trim()) errs.confirm_password = "Confirm your new password.";
    if (passwordData.new_password && passwordData.new_password.length < 8) errs.new_password = "Use at least 8 characters.";
    if (passwordData.new_password && passwordData.old_password && passwordData.new_password === passwordData.old_password) {
      errs.new_password = "New password must differ from the current one.";
    }
    if (passwordData.new_password !== passwordData.confirm_password) errs.confirm_password = "Passwords do not match.";

    setPwErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!validatePassword()) return;

    setChangingPassword(true);
    const payload = {
      old_password: passwordData.old_password.trim(),
      new_password: passwordData.new_password.trim(),
      confirm_password: passwordData.confirm_password.trim(),
    };

    try {
      const response = await fetchWithAuth("/api/auth/change-password", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setMessage("✅ Password changed successfully");
        setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
        setPwErrors({});
      } else {
        const err = await response.json();
        setError(`❌ Error: ${err?.detail || "Unable to change password"}`);
      }
    } catch {
      setError("❌ Network error");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <Helmet><title>Edit Profile - IndexBooster</title></Helmet>

      {/* Page header */}
      <div className="container-xl mt-3">
        <div className="d-flex align-items-center mb-3">
          <Button variant="light" size="sm" onClick={() => navigate(-1)} className="me-2">
            <IconCaretLeft size={18} />
          </Button>
          <div>
            <h2 className="m-0">Profile & Security</h2>
            <div className="text-secondary">Update your personal information and password.</div>
          </div>
        </div>
      </div>

      <div className="container-xl">
        {(error || message) && (
          <div className="mb-3">
            {error && <Alert variant="danger" className="mb-2"><IconAlertCircle size={16} className="me-2" />{error}</Alert>}
            {message && <Alert variant="success" className="mb-0"><IconCheck size={16} className="me-2" />{message}</Alert>}
          </div>
        )}

        <Row className="g-3 g-md-4">
          {/* Left: Personal info */}
          <Col lg={6}>
            <Card className="h-100">
              <div className="card-stamp">
                <div className="card-stamp-icon bg-primary">
                  <IconUser size={18} />
                </div>
              </div>
              <Card.Header>
                <Card.Title as="h3" className="m-0">Personal info</Card.Title>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-lg bg-primary-lt me-3">{initials}</span>
                      <div className="w-100">
                        <div className="placeholder col-6 mb-2"></div>
                        <div className="placeholder col-4"></div>
                      </div>
                    </div>
                    <div className="placeholder col-12 mb-3"></div>
                    <div className="placeholder col-12 mb-3"></div>
                    <div className="placeholder col-12 mb-3"></div>
                    <div className="d-flex gap-2">
                      <Button variant="primary" disabled><Spinner size="sm" className="me-2" />Loading…</Button>
                      <Button variant="outline-secondary" disabled>Reset</Button>
                    </div>
                  </>
                ) : (
                  <Form onSubmit={handleSubmitProfile}>
                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-lg bg-primary-lt me-3">{initials}</span>
                      <div>
                        <div className="h4 m-0">
                          {(formData.firstname || "Your").trim()} {(formData.lastname || "Name").trim()}
                        </div>
                        <div className="text-secondary">This information is visible only to you.</div>
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>First name</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleProfileChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Last name</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleProfileChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Select
                        name="country"
                        value={formData.country}
                        onChange={handleProfileChange}
                        required
                      >
                        <option value="">Select your country</option>
                        {countries.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button variant="primary" type="submit" disabled={savingProfile}>
                        {savingProfile ? (<><Spinner size="sm" className="me-2" />Saving…</>) : "Save Profile"}
                      </Button>
                      <Button variant="outline-secondary" type="button" onClick={handleResetProfile} disabled={savingProfile}>
                        Reset
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right: Change password */}
          <Col lg={6}>
            <Card className="h-100">
              <div className="card-stamp">
                <div className="card-stamp-icon bg-warning">
                  <IconShieldLock size={18} />
                </div>
              </div>
              <Card.Header>
                <Card.Title as="h3" className="m-0">Change password</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmitPassword} noValidate>
                  <Form.Group className="mb-3" controlId="old_password">
                    <Form.Label>Current password</Form.Label>
                    <Form.Control
                      type="password"
                      name="old_password"
                      value={passwordData.old_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!pwErrors.old_password}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{pwErrors.old_password}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="new_password">
                    <Form.Label>New password</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!pwErrors.new_password}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{pwErrors.new_password}</Form.Control.Feedback>
                    <div className="form-hint">Use 8+ characters. Avoid reusing your current password.</div>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="confirm_password">
                    <Form.Label>Confirm new password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      isInvalid={!!pwErrors.confirm_password}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{pwErrors.confirm_password}</Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button variant="danger" type="submit" disabled={changingPassword}>
                      {changingPassword ? (<><Spinner size="sm" className="me-2" />Saving…</>) : "Save Password"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => {
                        setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
                        setPwErrors({});
                      }}
                      disabled={changingPassword}
                    >
                      Clear
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Profile;
